/**
 * buildCierreTurnoResumen.ts — KPIs operativos compactos del turno POS para ticket de cierre.
 *
 * Top 5 productos + hora pico. Solo ventas activas del turno (sin créditos/web).
 */

import type { Venta } from "../types/caja.types";
import { formatCierreHourAmPm } from "./cierreCuadranteHelper";

export interface ProductoTopRow {
  rank: number;
  nombre: string;
  unidades: number;
  subtotal: number;
}

/**
 * Consolida TODOS los productos vendidos del turno (ventas activas), agrupados por
 * nombre, ordenados por facturación (subtotal) descendente y con rank 1..N.
 * Reutilizado por el Top 5 del ticket y por la sección "Productos vendidos" del detalle.
 */
export function consolidarProductosVendidos(ventas: Venta[]): ProductoTopRow[] {
  const activas = ventas.filter((v) => v.estado !== "anulada");
  const productoMap = new Map<string, { unidades: number; subtotal: number }>();
  for (const v of activas) {
    for (const item of v.productos ?? []) {
      const key = item.nombre?.trim() || "Producto";
      const qty = item.cantidad ?? 1;
      const line = Math.round((item.precioUnitario ?? 0) * qty);
      const row = productoMap.get(key) ?? { unidades: 0, subtotal: 0 };
      row.unidades += qty;
      row.subtotal += line;
      productoMap.set(key, row);
    }
  }
  return [...productoMap.entries()]
    .sort((a, b) => b[1].subtotal - a[1].subtotal)
    .map(([nombre, stats], i) => ({
      rank: i + 1,
      nombre,
      unidades: stats.unidades,
      subtotal: stats.subtotal,
    }));
}

export interface CierreTurnoResumen {
  horaPico: string | null;
  /** Franja de 1 h del bucket pico, p. ej. «08:00 A.M. - 09:00 A.M.». Null si no hubo ventas. */
  horaPicoRango: string | null;
  horaPicoVentasCount: number;
  horaPicoMonto: number;
  topProductos: ProductoTopRow[];
  /** Unidades vendidas en todo el turno, no solo en el Top 5. */
  totalArticulosVendidos: number;
  totalUnidadesTop5: number;
  ventasActivasCount: number;
}

function horaBucket(iso: string): string {
  const d = new Date(iso);
  const h = d.getHours();
  return `${String(h).padStart(2, "0")}:00`;
}

/** Agrupa ítems de ventas activas por nombre de producto. */
export function buildCierreTurnoResumen(ventas: Venta[]): CierreTurnoResumen {
  const activas = ventas.filter((v) => v.estado !== "anulada");

  const porHora = new Map<string, { count: number; monto: number }>();
  for (const v of activas) {
    const h = horaBucket(v.fecha);
    const row = porHora.get(h) ?? { count: 0, monto: 0 };
    row.count += 1;
    row.monto += v.subtotal ?? v.total ?? 0;
    porHora.set(h, row);
  }

  let horaPicoRaw = "";
  let horaPicoVentasCount = 0;
  let horaPicoMonto = 0;
  porHora.forEach((stats, h) => {
    if (stats.monto > horaPicoMonto) {
      horaPicoRaw = h;
      horaPicoVentasCount = stats.count;
      horaPicoMonto = stats.monto;
    }
  });

  const sinVentas = activas.length === 0;

  let horaPicoDate: Date | null = null;
  let horaPicoFinDate: Date | null = null;
  if (!sinVentas && horaPicoRaw) {
    const [hh] = horaPicoRaw.split(":").map(Number);
    horaPicoDate = new Date();
    horaPicoDate.setHours(hh, 0, 0, 0);
    horaPicoFinDate = new Date(horaPicoDate.getTime() + 60 * 60 * 1000);
  }

  const topProductos: ProductoTopRow[] = consolidarProductosVendidos(
    ventas,
  ).slice(0, 5);

  const totalArticulosVendidos = activas.reduce(
    (sum, venta) =>
      sum +
      (venta.productos ?? []).reduce(
        (subtotal, producto) => subtotal + (producto.cantidad ?? 1),
        0,
      ),
    0,
  );
  const totalUnidadesTop5 = topProductos.reduce((s, p) => s + p.unidades, 0);

  const horaPicoLabel = horaPicoDate
    ? formatCierreHourAmPm(horaPicoDate)
    : null;
  const horaPicoRango =
    horaPicoDate && horaPicoFinDate
      ? `${formatCierreHourAmPm(horaPicoDate)} - ${formatCierreHourAmPm(horaPicoFinDate)}`
      : null;
  return {
    horaPico: horaPicoLabel,
    horaPicoRango,
    horaPicoVentasCount,
    horaPicoMonto: Math.round(horaPicoMonto),
    topProductos,
    totalArticulosVendidos,
    totalUnidadesTop5,
    ventasActivasCount: activas.length,
  };
}
